import { ChangeDetectorRef, Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { LocaleService } from '@app/+authenticated/shared/locale/locale.service';
import { Store } from '@ngrx/store';
import { AppState } from '@reducers/interfaces';
import { StatusIconType } from '@sb/ui/status/status-mapping.helper';
import { formatDate, parseDate } from '@shiftbase-com/ts-shared/utilities';
import { Observable, of as observableOf } from 'rxjs';
import { debounceTime, filter, map, switchMap, tap } from 'rxjs/operators';

import { FormChange, formChange } from '../../../../../../forms/form.helper';
import { ValidationService } from '../../../../../../forms/validation.service';
import { CustomFieldsModel } from '../../../../../../reducers/orm/custom-fields/custom-fields.model';
import { DepartmentModel } from '../../../../../../reducers/orm/department/department.model';
import { LocationModel } from '../../../../../../reducers/orm/location/location.model';
import { RateCardModel } from '../../../../../../reducers/orm/rate-card/rate-card.model';
import { ScheduleModel } from '../../../../../../reducers/orm/schedule/schedule.model';
import { ShiftModel } from '../../../../../../reducers/orm/shift/shift.model';
import { filterShiftOptionsPerDepartment } from '../../../../../../reducers/orm/shift/shift.service';
import { TeamModel } from '../../../../../../reducers/orm/team/team.model';
import { filterTeamOptionsPerDepartment } from '../../../../../../reducers/orm/team/team.service';
import { TimesheetModel } from '../../../../../../reducers/orm/timesheet/timesheet.model';
import { getTimesheet, TimesheetService } from '../../../../../../reducers/orm/timesheet/timesheet.service';
import {
  TimesheetColumns,
  TimesheetCustomFieldsColumns,
} from '../../../../../../reducers/page-filters/page-filters.model';
import { lazySelect } from '../../../../../../shared/lazy-select.observable';
import { TimesheetScheduleDeviations } from '../../../interfaces';
import { useAutosaveRowHold } from '../autosave-row-hold';

@Component({
  standalone: false,
  selector: 'timesheet-row-clocked-in',
  templateUrl: './timesheet-row-clocked-in.component.html',
})
export class TimesheetRowClockedInComponent implements OnInit {
  #cd = inject(ChangeDetectorRef);
  #timesheetService = inject(TimesheetService);
  #store = inject<Store<AppState>>(Store);
  #destroyRef = inject(DestroyRef);
  // Until every change has been checked and saved: unmounting the row cancels the checks, the debounce
  // and the request.
  #autosaveHold = useAutosaveRowHold();
  #localeService = inject(LocaleService);

  @Input()
  timesheet: TimesheetModel;
  @Input()
  schedule?: ScheduleModel;
  @Input()
  customFields: CustomFieldsModel[] = [];

  @Input()
  locations: { [id: string]: LocationModel };
  @Input()
  departments: { [id: string]: DepartmentModel };
  @Input()
  departmentOptions: [
    {
      location: LocationModel;
      departments: DepartmentModel[];
    },
  ];

  #teams?: TeamModel[];
  @Input()
  set teams(teams: TeamModel[]) {
    this.#teams = teams;
    this.#setTeamOptions();
  }

  #shifts?: ShiftModel[];
  @Input()
  set shifts(shifts: ShiftModel[]) {
    this.#shifts = shifts;
    this.#setShiftOptions();
  }

  rateCardOptions: { value?: string; label: string }[];
  selectedRateCard: RateCardModel;

  @Input()
  set rateCards(rateCards: RateCardModel[]) {
    this.selectedRateCard = rateCards.find((rateCard) => rateCard.id == this.timesheet.rate_card_id);
    this.rateCardOptions = rateCards
      .filter((rateCard) => {
        if (rateCard.id === this.timesheet.rate_card_id) return true;

        return !rateCard.deleted;
      })
      .map((rateCard) => {
        let rateCardName = rateCard.name;

        if (rateCard.deleted) {
          const deletedAt = parseDate(rateCard.deleted_date);
          rateCardName += ' - ' + formatDate(deletedAt, this.#localeService.getCurrentLocale(), 'short');
        }

        return { value: rateCard.id, label: rateCardName };
      });
  }

  @Input()
  absenceWarning: boolean;
  @Input()
  doubleRegistration: boolean;
  @Input()
  deviationsFromSchedule: TimesheetScheduleDeviations;

  @Input()
  cols: TimesheetColumns;
  @Input()
  customFieldCols: TimesheetCustomFieldsColumns;

  form = new UntypedFormGroup({
    id: new UntypedFormControl('', Validators.required),
    date: new UntypedFormControl('', { validators: [Validators.required], updateOn: 'blur' }),
    starttime: new UntypedFormControl('', {
      validators: [Validators.required, ValidationService.timeValidator],
      updateOn: 'blur',
    }),
    kilometers: new UntypedFormControl('', {
      validators: [Validators.required, ValidationService.decimalValidator],
      updateOn: 'blur',
    }),
    meals: new UntypedFormControl('', {
      validators: [Validators.required, ValidationService.numberValidator],
      updateOn: 'blur',
    }),
    note: new UntypedFormControl('', { updateOn: 'blur' }),
    user_id: new UntypedFormControl('', Validators.required),
    department_id: new UntypedFormControl('', Validators.required),
    shift_id: new UntypedFormControl('', Validators.required),
    team_id: new UntypedFormControl('', Validators.required),
    rate_card_id: new UntypedFormControl('', Validators.required),
  });

  selectedDepartment: DepartmentModel;
  selectedLocation: LocationModel;

  teamOptions: { value: string; label: string }[];
  shiftOptions: { value: string; label: string }[];

  #internalChange = false;

  ngOnInit(): void {
    this.#setDepartment(this.timesheet.department_id);
    this.#initForm();

    this.#store
      .pipe(
        lazySelect(getTimesheet(this.timesheet.id)),
        filter(() => !this.#internalChange),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe((data) => {
        this.form.patchValue(data, { emitEvent: false });
      });
  }

  onNoteChange(value: string): void {
    this.form.controls['note'].setValue(value);
  }

  #initForm() {
    const formResetOptions = {
      onlySelf: true,
      emitEvent: false,
    };

    this.form.reset(this.timesheet, formResetOptions);

    formChange(this.form)
      .pipe(
        map((formChange) => ({ formChange, change: this.#autosaveHold.changed() })),
        switchMap(({ formChange, change }) =>
          this.#checkChange(formChange).pipe(map((checked) => ({ checked, change }))),
        ),
        tap(({ checked, change }) => {
          if (checked === false) {
            this.#autosaveHold.rejected(change);
          } else {
            this.#autosaveHold.passed(change);
          }
        }),
        filter((item): item is { checked: FormChange; change: number } => item.checked !== false),
        debounceTime(200),
        tap(() => (this.#internalChange = true)),
        switchMap(({ checked, change }) => this.#autosaveHold.save(change, this.#saveTimesheet(checked))),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(
        () => {
          this.#cd.markForCheck();
          this.#internalChange = false;
        },
        () => {
          this.#internalChange = false;
          this.#autosaveHold.stopped();
        },
      );
  }

  #checkChange(formChange: FormChange): Observable<FormChange | boolean> {
    if (formChange.control === 'department_id') {
      return this.#changeDepartment(formChange.controlValue);
    }

    if (formChange.control === 'shift_id') {
      return this.#checkRateCard(formChange);
    }

    if (!formChange.formValid) {
      return observableOf(false);
    }

    return observableOf(formChange);
  }

  #checkRateCard(formChange: FormChange): Observable<FormChange | boolean> {
    //check starttime and endtime values
    if (!this.form.controls['shift_id'].valid) {
      return observableOf(false);
    }

    return this.#timesheetService.checkRateCard(formChange.formValue).pipe(
      map((response) => {
        if (response.rate_card_id === false) {
          return formChange;
        }

        const control = this.form.controls['rate_card_id'];
        if (response.rate_card_id == control.value) {
          return formChange;
        }

        control.setValue(response.rate_card_id);
        return false;
      }),
    );
  }

  #saveTimesheet(formChange: FormChange) {
    if (!formChange.formValid) {
      return observableOf(false);
    }

    return this.#timesheetService.save(formChange.formValue);
  }

  #changeDepartment(departmentId: string) {
    if (this.selectedDepartment.id === departmentId) {
      return observableOf(false);
    }

    this.form.controls['team_id'].setValue(null);
    this.form.controls['shift_id'].setValue(null);

    this.#setDepartment(departmentId);
    this.#cd.markForCheck();

    return observableOf(false);
  }

  #setDepartment(departmentId: string) {
    this.form.controls['department_id'].patchValue(departmentId);

    this.selectedDepartment = this.departments[departmentId];

    if (!this.selectedDepartment) {
      return;
    }

    this.selectedLocation = this.locations[this.selectedDepartment.location_id];

    this.#setTeamOptions();
    this.#setShiftOptions();
  }

  changeStatus(status: StatusIconType) {
    this.form.controls['status'].patchValue(status);
  }

  #setTeamOptions() {
    if (!this.form.controls['department_id'].value || !this.#teams) {
      return;
    }

    const teamFilter = filterTeamOptionsPerDepartment(
      this.form.controls['department_id'].value,
      this.timesheet.team_id,
    );

    this.teamOptions = teamFilter(this.#teams).map((team) => ({ value: team.id, label: team.name }));

    const teamControl = this.form.controls['team_id'];

    //set team if only 1 option
    if (this.teamOptions.length === 1) {
      const teamToSelect = this.teamOptions[0].value;

      if (teamControl.value !== teamToSelect) {
        teamControl.setValue(teamToSelect);
      }
    }

    const ids = this.teamOptions.map((option) => option.value);

    if (this.teamOptions.length > 1 && ids.indexOf(teamControl.value) === -1) {
      teamControl.setValue('');
    }
  }

  #setShiftOptions() {
    if (!this.form.controls['department_id'].value || !this.#shifts) {
      return;
    }

    const shiftFilter = filterShiftOptionsPerDepartment(
      this.form.controls['department_id'].value,
      this.timesheet.shift_id,
    );

    this.shiftOptions = shiftFilter(this.#shifts).map((shift) => ({ value: shift.id, label: shift.long_name }));

    const shiftControl = this.form.controls['shift_id'];

    //set shift if only 1 option
    if (this.shiftOptions.length === 1) {
      const shiftToSelect = this.shiftOptions[0].value;

      if (shiftControl.value !== shiftToSelect) {
        shiftControl.setValue(shiftToSelect);
      }
    }

    const ids = this.shiftOptions.map((option) => option.value);

    if (this.shiftOptions.length > 1 && ids.indexOf(shiftControl.value) === -1) {
      shiftControl.setValue('');
    }
  }
}
